import Badge from "@mui/material/Badge";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Link } from "react-router-dom";
import { useSaleDepositEditRequestCount } from "../../utils/hooks/useSaleDepositEditRequestCount";
import {
  SALE_DEPOSIT_EDIT_REQUEST_PATH,
  SALE_DEPOSIT_REPORT_PATH,
} from "./depositReportUrl";

/** The heading both tabs sit under: they are two views of one screen, so one title serves. */
export const SALE_DEPOSIT_REPORT_TITLE = "รายงานการโอน (Sale Deposit Report)";

/**
 * Which tab is on screen. The tab's own path IS its value, so the highlighted tab and the
 * URL it points at cannot drift apart.
 */
export type SaleDepositReportTab =
  | typeof SALE_DEPOSIT_REPORT_PATH
  | typeof SALE_DEPOSIT_EDIT_REQUEST_PATH;

const TRANSFER_LIST_TAB_LABEL = "รายการโอน";
const EDIT_REQUEST_TAB_LABEL = "คำขอแก้ไขยอดโอน";

/**
 * The pending count sits after the label rather than floating over its corner: a tab label
 * is a line of Thai text, and MUI's default corner badge would land on its last character.
 *
 * Laying it out in the line is also why a zero count may not render an empty Badge: MUI hides
 * one by scaling it away, which a statically positioned badge still holds width for, and the
 * label would sit off-centre against its neighbour with nothing to show for it.
 */
const PENDING_BADGE_SX = {
  "& .MuiBadge-badge": {
    position: "static",
    transform: "none",
    ml: 1,
    fontWeight: 700,
  },
} as const;

interface ReportTabsProps {
  current: SaleDepositReportTab;
}

/**
 * The two halves of รายงานการโอน.
 *
 * คำขอแก้ไขยอดโอน used to be its own entry under รายงาน, which is where the tester objected
 * to it: it is an approval queue, not a report, and it reads the very deposits the transfer
 * report lists. It belongs here, one tab away from the rows it is about.
 *
 * Each tab is a real link to its own URL, so a tab can be sent to a colleague and the
 * browser's back button walks between them. The drill-down into one branch-day deliberately
 * gets none of this: it is a detail view reached from inside a tab and carries its own
 * ย้อนกลับ back to the filters it was opened from.
 */
export default function SaleDepositReportTabs({
  current,
}: Readonly<ReportTabsProps>) {
  // Zero for everyone but the grade that can actually answer a request — the badge is a
  // count of work waiting for the reader, and the branch cannot decide its own request.
  const pendingEditRequestCount = useSaleDepositEditRequestCount();

  // The bare label while the count is zero, which is almost always: a badge that always
  // shows something is a badge nobody reads.
  const editRequestTabLabel =
    pendingEditRequestCount > 0 ? (
      <Badge
        color="error"
        badgeContent={pendingEditRequestCount}
        sx={PENDING_BADGE_SX}
      >
        {EDIT_REQUEST_TAB_LABEL}
      </Badge>
    ) : (
      EDIT_REQUEST_TAB_LABEL
    );

  return (
    <Tabs value={current}>
      <Tab
        component={Link}
        to={SALE_DEPOSIT_REPORT_PATH}
        value={SALE_DEPOSIT_REPORT_PATH}
        label={TRANSFER_LIST_TAB_LABEL}
      />
      <Tab
        component={Link}
        to={SALE_DEPOSIT_EDIT_REQUEST_PATH}
        value={SALE_DEPOSIT_EDIT_REQUEST_PATH}
        label={editRequestTabLabel}
      />
    </Tabs>
  );
}
